import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  TablePagination,
  TextField,
  Tooltip,
  Autocomplete,
  Typography,
} from "@mui/material";
import { Save, MoreVert, CheckCircle } from "@mui/icons-material";
import DataTable from "../../../Components/Common/datatable/DataTable";
import ButtonComponent from "../../../Components/Common/button/Button";
import ServerTablePaginationActions from "../../../Components/Common/pagination/ServerTablePaginationActions";
import { useServerPagination } from "../../../utils/useServerPagination";
import {
  fetchDropdownData,
  fetchRealignmentRecords,
  updateRealignmentRecord,
  approveBatchMasterData,
  approveBatchRealignment,
  rejectBulkRealignment,
  clearSuccessMessage,
  clearError,
  clearSaveStatus,
  setSelectedRecords,
} from "../../../redux/realignmentSlice";

const RealignmentList = () => {
  const dispatch = useDispatch();
  const gridRef = useRef();
  const dashboardStatusFilter = "PENDING,FAILED";

  // Redux state
  const {
    pendingRecords,
    dropdownData,
    loading,
    dropdownLoading,
    savingRecord,
    error,
    successMessage,
    saveStatus,
    saveStatusType,
    currentPage,
    pageSize,
    totalElements,
    totalPages,
    selectedRecords,
    mdApprovalStatus,
  } = useSelector((state) => state.realignment);

  // State management for editable fields
  // Stored in refs so mutations don't trigger table re-renders
  const editedValuesRef = useRef({});
  const editedRecordIdsRef = useRef(new Set());
  const [selectedRows, setSelectedRows] = useState([]);
  const [rejectDialog, setRejectDialog] = useState({
    open: false,
    recordIds: [],
    remarks: "",
  });
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [approveConfirmOpen, setApproveConfirmOpen] = useState({
    open: false,
    actionType: "", // "MD" or "REALIGNMENT"
  });
  const [masterDataApprovalStatus, setMasterDataApprovalStatus] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeEditRowId, setActiveEditRowId] = useState(null);
  const [filters, setFilters] = useState({
  location: [],
  region: [],
  kit: [],
});
  const {
    paginationModel,
    setPage,
    setPageSize,
  } = useServerPagination({
    initialPage: 0,
    initialPageSize: 10,
  });

  // Initialize with record data when records are loaded
  useEffect(() => {
    if (pendingRecords && pendingRecords.length > 0) {
      const newEditedValues = {};
      const approvedStatusMap = {};

      pendingRecords.forEach((record) => {
        newEditedValues[`newLocation_${record.id}`] = record.newLocation || "";
        newEditedValues[`newLocationDesc_${record.id}`] =
          record.newLocationDescription || "";
        newEditedValues[`newProfitCenter_${record.id}`] =
          record.newProfitCenter || "";
        newEditedValues[`newProfitCenterDesc_${record.id}`] =
          record.newProfitCenterDescription || "";
        newEditedValues[`newForecastPartner_${record.id}`] =
          record.newForecastPartner || "";
        newEditedValues[`newForecastPartnerDesc_${record.id}`] =
          record.newForecastPartnerDescription || "";
        newEditedValues[`sourceKeyFigurePercentage_${record.id}`] =
          record.sourceKeyFigurePercentage || "";

        if (record.masterDataApprovalStatus === "APPROVED") {
          approvedStatusMap[record.id] = true;
        }
      });

      setMasterDataApprovalStatus(approvedStatusMap);
      // setEditedValues(newEditedValues);
    } else {
      setMasterDataApprovalStatus({});
    }
  }, [pendingRecords]);

  // Fetch dropdown data on component mount
  useEffect(() => {
    dispatch(fetchDropdownData());
  }, [dispatch]);

  // Fetch records whenever server pagination model changes
  useEffect(() => {
    dispatch(
      fetchRealignmentRecords({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        status: dashboardStatusFilter,
      }),
    );
  }, [dispatch, paginationModel.page, paginationModel.pageSize, dashboardStatusFilter]);

  const refetchCurrentPage = useCallback(() => {
    dispatch(
      fetchRealignmentRecords({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        status: dashboardStatusFilter,
      }),
    );
  }, [dispatch, paginationModel.page, paginationModel.pageSize, dashboardStatusFilter]);

  // Handle success message
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(clearSuccessMessage());
    }
  }, [successMessage, dispatch]);

  // Handle error
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Handle save status display
  useEffect(() => {
    if (saveStatus) {
      if (saveStatusType === "success") {
        toast.success(saveStatus);
      } else {
        toast.error(saveStatus);
      }
      const timer = setTimeout(() => {
        dispatch(clearSaveStatus());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [saveStatus, saveStatusType, dispatch]);

  //   useEffect(() => {
  //   if (saveStatus && saveStatusType === "success") {
  //     setActiveEditRowId(null);
  //     setEditedRecordIds(new Set());
  //   }
  // }, [saveStatus, saveStatusType]);

  // Dropdown options from API
  const locationOptions = dropdownData.locations || [];
  const profitCenterOptions = dropdownData.profitCenters || [];
  const forecastPartnerOptions = dropdownData.forecastPartners || [];

  // Helper function to get location name by ID
  const getLocationNameById = (locationId) => {
    if (!locationId) return "-";
    const location = locationOptions.find(
      (loc) => loc.locationId === locationId,
    );
    return location
      ? `${location.locationId}-${location.location}`
      : locationId;
  };

  // Helper function to get profit center description by ID
  const getProfitCenterDescById = (profitCenterId) => {
    if (!profitCenterId) return "-";
    const pc = profitCenterOptions.find(
      (p) => p.profitCenterId === profitCenterId,
    );
    return pc
      ? `${pc.profitCenterId}-${pc.profitCenterDescription}`
      : profitCenterId;
  };

  // Helper function to get forecast partner description by ID
  const getForecastPartnerDescById = (customerId) => {
    if (!customerId) return "-";
    const fp = forecastPartnerOptions.find((f) => f.customerId === customerId);
    return fp ? `${fp.customerId}-${fp.customerDescription}` : customerId;
  };

  const isRowEditable = (rowId) => {
    if (masterDataApprovalStatus[rowId] === true) return false;
    return activeEditRowId === null || activeEditRowId === rowId;
  };

  // Autocomplete renderer for New Location
  const NewLocationRenderer = (params) => {
    const rowId = params.data.id;
    const key = `newLocation_${rowId}`;
    const [localValue, setLocalValue] = useState(
      () => editedValuesRef.current[key] !== undefined
        ? editedValuesRef.current[key]
        : params.data.newLocation ?? ""
    );

    const selectedLocation =
      localValue && localValue !== ""
        ? locationOptions.find(
            (loc) => String(loc.locationId) === String(localValue),
          )
        : null;

    const disabled = !isRowEditable(params.data.id);

    return (
      <Autocomplete
        disabled={disabled}
        options={locationOptions}
        getOptionLabel={(option) => {
          if (!option) return "";
          if (typeof option === "string") return option;
          return option.location
            ? `${option.locationId}-${option.location}`
            : option.locationId || "";
        }}
        value={selectedLocation ?? (localValue || null)}
        onFocus={() => {
          if(activeEditRowId===null)setActiveEditRowId(rowId)}}
        onChange={(e, newValue) => {
          const val = newValue ? newValue.locationId ?? newValue : "";
          setLocalValue(val);
          editedValuesRef.current[key] = val;
          editedValuesRef.current[`newLocationDesc_${rowId}`] = newValue ? newValue.location : "";
          editedRecordIdsRef.current.add(rowId);
        }}
        isOptionEqualToValue={(option, value) => {
          if (!value) return false;
          if (typeof value === "string") {
            return String(option.locationId) === String(value);
          }
          return String(option.locationId) === String(value.locationId);
        }}
        freeSolo
        disableClearable={false}
        size="small"
        sx={{
          width: "100%",
          "& .MuiInputBase-root": {
            fontSize: "0.75rem",
            padding: "2px 4px",
            height: "39px",
          },
          "& .MuiOutlinedInput-input": {
            padding: "6px 4px",
          },
        }}
        ListboxProps={{
          style: { maxHeight: "200px", fontSize: "0.75rem" },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search"
            size="small"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                padding: "2px",
              },
              "& .MuiOutlinedInput-input": {
                padding: "6px 4px",
                fontSize: "0.75rem",
              },
            }}
          />
        )}
      />
    );
  };

  // Autocomplete renderer for New Profit Center
  const NewProfitCenterRenderer = (params) => {
    const rowId = params.data.id;
    const key = `newProfitCenter_${rowId}`;
    const [localValue, setLocalValue] = useState(
      () => editedValuesRef.current[key] !== undefined
        ? editedValuesRef.current[key]
        : params.data.newProfitCenter ?? ""
    );

    const selectedPC =
      localValue && localValue !== ""
        ? profitCenterOptions.find(
            (pc) => String(pc.profitCenterId) === String(localValue),
          )
        : null;

    const disabled = !isRowEditable(params.data.id);

    return (
      <Autocomplete
        disabled={disabled}
        options={profitCenterOptions}
        getOptionLabel={(option) => {
          if (!option) return "";
          if (typeof option === "string") return option;
          return option.profitCenterDescription
            ? `${option.profitCenterId}-${option.profitCenterDescription}`
            : option.profitCenterId || "";
        }}
        value={selectedPC ?? (localValue || null)}
        onFocus={() => {
          if (activeEditRowId === null) setActiveEditRowId(rowId);}}
        onChange={(e, newValue) => {
          const val = newValue ? newValue.profitCenterId ?? newValue : "";
          setLocalValue(val);
          editedValuesRef.current[key] = val;
          editedValuesRef.current[`newProfitCenterDesc_${rowId}`] = newValue ? newValue.profitCenterDescription : "";
          editedRecordIdsRef.current.add(rowId);
        }}
        isOptionEqualToValue={(option, value) => {
          if (!value) return false;
          if (typeof value === "string") {
            return String(option.profitCenterId) === String(value);
          }
          return String(option.profitCenterId) === String(value.profitCenterId);
        }}
        freeSolo
        disableClearable={false}
        size="small"
        sx={{
          width: "100%",
          "& .MuiInputBase-root": {
            fontSize: "0.75rem",
            padding: "2px 4px",
            height: "39px",
          },
          "& .MuiOutlinedInput-input": {
            padding: "6px 4px",
          },
        }}
        ListboxProps={{
          style: { maxHeight: "200px", fontSize: "0.75rem" },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search"
            size="small"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                padding: "2px",
              },
              "& .MuiOutlinedInput-input": {
                padding: "6px 4px",
                fontSize: "0.75rem",
              },
            }}
          />
        )}
      />
    );
  };

  // Autocomplete renderer for New Forecast Partner
  const NewForecastPartnerRenderer = (params) => {
    const rowId = params.data.id;
    const key = `newForecastPartner_${rowId}`;
    const [localValue, setLocalValue] = useState(
      () => editedValuesRef.current[key] !== undefined
        ? editedValuesRef.current[key]
        : params.data.newForecastPartner ?? ""
    );

    const selectedFP =
      localValue && localValue !== ""
        ? forecastPartnerOptions.find(
            (fp) => String(fp.customerId) === String(localValue),
          )
        : null;

    const disabled = !isRowEditable(params.data.id);

    return (
      <Autocomplete
        disabled={disabled}
        options={forecastPartnerOptions}
        getOptionLabel={(option) => {
          if (!option) return "";
          if (typeof option === "string") return option;
          return option.customerDescription
            ? `${option.customerId}-${option.customerDescription}`
            : option.customerId || "";
        }}
        value={selectedFP ?? (localValue || null)}
        onFocus={() => setActiveEditRowId(rowId)}
        onChange={(e, newValue) => {
          const val = newValue ? newValue.customerId ?? newValue : "";
          setLocalValue(val);
          editedValuesRef.current[key] = val;
          editedValuesRef.current[`newForecastPartnerDesc_${rowId}`] = newValue ? newValue.customerDescription : "";
          editedRecordIdsRef.current.add(rowId);
        }}
        isOptionEqualToValue={(option, value) => {
          if (!value) return false;
          if (typeof value === "string") {
            return String(option.customerId) === String(value);
          }
          return String(option.customerId) === String(value.customerId);
        }}
        freeSolo
        disableClearable={false}
        size="small"
        sx={{
          width: "100%",
          "& .MuiInputBase-root": {
            fontSize: "0.75rem",
            padding: "2px 4px",
            height: "39px",
          },
          "& .MuiOutlinedInput-input": {
            padding: "6px 4px",
          },
        }}
        ListboxProps={{
          style: { maxHeight: "200px", fontSize: "0.75rem" },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search"
            size="small"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                padding: "2px",
              },
              "& .MuiOutlinedInput-input": {
                padding: "6px 4px",
                fontSize: "0.75rem",
              },
            }}
          />
        )}
      />
    );
  };

  // Source Key Figure Percentage renderer
  const SourceKeyFigureRenderer = (params) => {
    const rowId = params.data.id;
    const key = `sourceKeyFigurePercentage_${rowId}`;
    const disabled = !isRowEditable(rowId);

    return (
      <input
        type="number"
        min={0}
        max={100}
        disabled={disabled}
        defaultValue={
          editedValuesRef.current[key] ?? params.data.sourceKeyFigurePercentage ?? ""
        }
        onFocus={() => {if(activeEditRowId===null)setActiveEditRowId(rowId)}}
        onBlur={(e) => {
          const val = Math.min(100, Math.max(0, Number(e.target.value)));
          e.target.value = val;
          editedValuesRef.current[key] = val;
          editedRecordIdsRef.current.add(rowId);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            // e.target.blur(); // ✅ commit on Enter
          }
        }}
        style={{
          width: "100%",
          // height: "32px",
          padding: "4px",
          border: "0.5px solid #ccc",
          borderRadius: "2px",
          fontSize: "0.875rem",
          backgroundColor: "transparent",
        }}
      />
    );
  };

  // Status renderer - only show status text
  const StatusRenderer = (params) => {
    const status = params.data.realignmentStatus || "PENDING";
    const statusColor =
      status === "APPROVED"
        ? "#4caf50"
        : status === "REJECTED"
          ? "#f44336"
          : "#ff9800";

    return (
      <span style={{ color: statusColor, fontWeight: "bold" }}>{status}</span>
    );
  };

  // Master Data Status renderer
  const MasterDataStatusRenderer = (params) => {
    const isMdApproved = mdApprovalStatus[params.data.id] === true;
    const masterDataStatus = params.data.masterDataApprovalStatus=="APPROVED" ? "APPROVED" : params.data.masterDataApprovalStatus;

    const statusColor =
      masterDataStatus === "APPROVED"
        ? "#4caf50"
        : masterDataStatus === "REJECTED"
          ? "#f44336"
          : masterDataStatus === "FAILED"
          ? "#f44336"
          :"#ff9800";

    if (isMdApproved) {
      return (
        <span style={{ color: statusColor, fontWeight: "bold" }}>
          {masterDataStatus}
        </span>
      );
    }
    return (
      <span style={{ color: statusColor, fontWeight: "bold" }}>
        {masterDataStatus}
      </span>
    );
  };

  // Handle save action
  const handleSave = () => {
    console.log("handleSave clicked");
    if (editedRecordIdsRef.current.size === 0) {
      console.log("handleSave clicked but no records edited");
      toast.warning("Please edit at least one record to save");
      return;
    }

    // Validate all edited records
    const recordsToSave = Array.from(editedRecordIdsRef.current);
    for (const recordId of recordsToSave) {
      const record = pendingRecords.find((r) => r.id === recordId);
      if (!record) continue;

      const updateData = {
        newLocation:
          editedValuesRef.current[`newLocation_${recordId}`] !== undefined
            ? editedValuesRef.current[`newLocation_${recordId}`]
            : record.newLocation,
        newLocationDescription:
          editedValuesRef.current[`newLocationDesc_${recordId}`] !== undefined
            ? editedValuesRef.current[`newLocationDesc_${recordId}`]
            : record.newLocationDescription,
        newProfitCenter:
          editedValuesRef.current[`newProfitCenter_${recordId}`] !== undefined
            ? editedValuesRef.current[`newProfitCenter_${recordId}`]
            : record.newProfitCenter,
        newProfitCenterDescription:
          editedValuesRef.current[`newProfitCenterDesc_${recordId}`] !== undefined
            ? editedValuesRef.current[`newProfitCenterDesc_${recordId}`]
            : record.newProfitCenterDescription,
        newForecastPartner:
          editedValuesRef.current[`newForecastPartner_${recordId}`] !== undefined
            ? editedValuesRef.current[`newForecastPartner_${recordId}`]
            : record.newForecastPartner,
        newForecastPartnerDescription:
          editedValuesRef.current[`newForecastPartnerDesc_${recordId}`] !== undefined
            ? editedValuesRef.current[`newForecastPartnerDesc_${recordId}`]
            : record.newForecastPartnerDescription,
        sourceKeyFigurePercentage:
          editedValuesRef.current[`sourceKeyFigurePercentage_${recordId}`] !== undefined
            ? parseInt(editedValuesRef.current[`sourceKeyFigurePercentage_${recordId}`])
            : record.sourceKeyFigurePercentage,
      };

      // Check if required fields are empty after update
      const missingFields = [];
      if (!updateData.newLocation || updateData.newLocation === "")
        missingFields.push("New Location");
      if (!updateData.newProfitCenter || updateData.newProfitCenter === "")
        missingFields.push("New Profit Center");
      if (
        !updateData.newForecastPartner ||
        updateData.newForecastPartner === ""
      )
        missingFields.push("New Forecast Partner");
      if (
        updateData.sourceKeyFigurePercentage === "" ||
        isNaN(updateData.sourceKeyFigurePercentage)
      ) {
        missingFields.push("Source Key Figure %");
      } else if (updateData.sourceKeyFigurePercentage > 100) {
        missingFields.push("Source Key Figure % (must be ≤ 100)");
      }

      if (missingFields.length > 0) {
        console.log(
          "Missing fields for record ID",
          recordId,
          ":",
          missingFields,
        );
        toast.warning(
          `Please provide value(s) for ${missingFields.join(", ")} in the record`,
        );
        return;
      }

      dispatch(updateRealignmentRecord({ recordId, updateData }));
    }

    // Clear edited record IDs after save is triggered
    setActiveEditRowId(null);
    editedValuesRef.current = {};
    editedRecordIdsRef.current = new Set();
    setSaveConfirmOpen(false);
  };

  // Validate Master Data Approval before approving
  const validateMasterDataApproval = () => {
    const invalidRecords = [];

    for (const recordId of selectedRows) {
      const record = pendingRecords.find((r) => r.id === recordId);
      if (!record) continue;

      const newLocation =
        editedValuesRef.current[`newLocation_${recordId}`] ?? record.newLocation;
      const newProfitCenter =
        editedValuesRef.current[`newProfitCenter_${recordId}`] ?? record.newProfitCenter;
      const newForecastPartner =
        editedValuesRef.current[`newForecastPartner_${recordId}`] ??
        record.newForecastPartner;
      const skf =
        editedValuesRef.current[`sourceKeyFigurePercentage_${recordId}`] ??
        record.sourceKeyFigurePercentage;

      if (
        !newLocation ||
        !newProfitCenter ||
        !newForecastPartner ||
        skf === "" ||
        isNaN(skf) ||
        Number(skf) <= 0
      ) {
        invalidRecords.push(record.flowProjectId);
      }
    }

    if (invalidRecords.length > 0) {
      toast.warning(
        `Provide values for empty fields and ensure Source Key Figure % > 0 for record(s): ${invalidRecords.join(", ")}`,
      );
      return false;
    }

    return true;
  };

  // Handle approve action
  const handleApprove = (actionType) => {
    if (selectedRows.length === 0) {
      handleActionMenuClose();
      toast.warning("Please select at least one record");
      return;
    }

    if (actionType === "MD") {
      const isValid = validateMasterDataApproval();
      handleActionMenuClose();
      if (!isValid) return;

      // dispatch(approveBatchMasterData({ recordIds: selectedRows }));
      dispatch(approveBatchMasterData({ recordIds: selectedRows }))
    .unwrap()
    .then(() => {
      refetchCurrentPage();
    })
    .catch((err) => {
      toast.error(err?.message || "Master Data approval failed");
    });
    } else if (actionType === "REALIGNMENT") {
      dispatch(approveBatchRealignment({ recordIds: selectedRows }));
    }

    handleActionMenuClose();
    setApproveConfirmOpen({ open: false, actionType: "" });
    setSelectedRows([]);
    setAnchorEl(null);
  };

  // Handle reject action
  const handleRejectOpen = () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one record");
      return;
    }

    setRejectDialog({
      open: true,
      recordIds: selectedRows,
      remarks: "",
    });
    handleActionMenuClose();
  };

  // Handle reject confirm
  const handleRejectConfirm = () => {
    if (!rejectDialog.remarks.trim()) {
      toast.warning("Please enter rejection remarks");
      return;
    }

    dispatch(
      rejectBulkRealignment({
        ids: rejectDialog.recordIds,
        rejectionRemarks: rejectDialog.remarks,
      }),
    );

    setRejectDialog({ open: false, recordIds: [], remarks: "" });
    setSelectedRows([]);
  };

  // Handle action menu click
  const handleActionMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle action menu close
  const handleActionMenuClose = () => {
    setAnchorEl(null);
  };

  // Check if realignment option should be disabled
  const isRealignmentDisabled = selectedRows.some((id) => {
    return masterDataApprovalStatus[id] !== true;
  });

  // Check if Master Data option should be disabled
  const isMasterDataDisabled = selectedRows.some((id) => {
    return masterDataApprovalStatus[id] == true;
  });

  const commonColumns = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        field: "supportCheckbox",
        headerName: "",
        sortable: false,
        width: 25,
        suppressSizeToFit: true,
        cellStyle: { textAlign: "left" },
      },
      {
        field: "flowProjectId",
        headerName: "Project ID",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
        },
        filter: "agTextColumnFilter",
        width: 200,
        cellRenderer: (params) => (
          <Tooltip title={params.data.flowProjectId} arrow>
            <span>{params.data.flowProjectId}</span>
          </Tooltip>
        ),
        cellStyle: { textAlign: "left" },
      },
      {
        field: "oldKit",
        headerName: "Old Kit",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
        },
        filter: "agTextColumnFilter",
        width: 150,
        cellRenderer: (params) => {
        const oldKit = params?.data?.oldKit ?? "";
        const oldKitDescription = params?.data?.oldPrdDesc ?? "";
        const displayValue = oldKit || oldKitDescription
          ? `${oldKit}${oldKit && oldKitDescription ? "-" : ""}${oldKitDescription}`
          : "-";
        
        return <Tooltip title={displayValue} arrow>
          <span>{displayValue}</span>
        </Tooltip>
        },
        cellStyle: { textAlign: "left" },
      },
      {
        field: "location",
        headerName: "Original Location",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
        },
        filter: "agTextColumnFilter",
        width: 200,
        cellRenderer: (params) => {
          const locationName = getLocationNameById(params.data.location);
          return (
            <Tooltip title={locationName} arrow>
              <span>{locationName}</span>
            </Tooltip>
          );
        },
        cellStyle: { textAlign: "left" },
      },
      {
        field: "countryOfSales",
        headerName: "Country of Sales",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
        },
        filter: "agTextColumnFilter",
        width: 200,
        cellRenderer: (params) => (
          <span>{params.data.countryOfSales || "-"}</span>
        ),
        cellStyle: { textAlign: "left" },
      },
      {
      field: "region",
      headerName: "Region",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 200,
      cellRenderer: (params) => <span>{params.data.region || "-"}</span>,
      cellStyle: { textAlign: "left" },
    },
      {
        field: "projectType",
        headerName: "Project Type",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
        },
        filter: "agTextColumnFilter",
        width: 200,
        cellRenderer: (params) => {
          const projectType = params.data.projectType || "";
          const projectTypeDesc = params.data.projectTypeDesc || "";
          const displayValue = projectType
            ? projectTypeDesc
              ? `${projectType} - ${projectTypeDesc}`
              : projectType
            : "-";

          return <span>{displayValue}</span>;
        },
        cellStyle: { textAlign: "left" },
      },
      {
        field: "oldProfitCenter",
        headerName: "Original Profit Center",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
        },
        filter: "agTextColumnFilter",
        width: 200,
        cellRenderer: (params) => {
          const oldProfitCenter = params?.data?.oldProfitCenter ?? "";
          const oldProfitCenterDescription = params?.data?.oldProfitCenterDescription ?? "";
          const displayValue = oldProfitCenter || oldProfitCenterDescription
            ? `${oldProfitCenter}${oldProfitCenter && oldProfitCenterDescription ? "-" : ""}${oldProfitCenterDescription}`
            : "-";

          return <Tooltip title={displayValue} arrow>
                  <span>{displayValue}</span>
                </Tooltip>;
        },
        cellStyle: { textAlign: "left" },
      },
      {
        field: "oldForecastPartner",
        headerName: "Original Forecast Partner",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
        },
        filter: "agTextColumnFilter",
        width: 200,
        cellRenderer: (params) => {
          const oldForecastPartner = params?.data?.oldForecastPartner ?? "";
          const oldForecastPartnerDescription = params?.data?.oldForecastPartnerDescription ?? "";
          const displayValue = oldForecastPartner || oldForecastPartnerDescription
            ? `${oldForecastPartner}${oldForecastPartner && oldForecastPartnerDescription ? "-" : ""}${oldForecastPartnerDescription}`
            : "-";

          return <Tooltip title={displayValue} arrow>
                  <span>{displayValue}</span>
                </Tooltip>;
        },
        cellStyle: { textAlign: "left" },
      },
      {
        field: "beverageProductDescription",
        headerName: "Beverage Product Description",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
        },
        filter: "agTextColumnFilter",
        width: 225,
        cellRenderer: (params) => {
          const beverageProductId = params?.data?.beverageProductId ?? "";
          const beverageProductDescription = params?.data?.beverageProductDescription ?? "";
          const displayValue = beverageProductId || beverageProductDescription
            ? `${beverageProductId}${beverageProductId && beverageProductDescription ? "-" : ""}${beverageProductDescription}`
            : "-";
          return (
            <Tooltip title={displayValue} arrow>
              <span>{displayValue}</span>
            </Tooltip>
          );
        },
        cellStyle: { textAlign: "left" },
      },
      {
        field: "newKit",
        headerName: "New Kit",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
        },
        filter: "agTextColumnFilter",
        width: 150,
        cellRenderer: (params) => {
        const newKit = params?.data?.newKit ?? "";
        const newKitDescription = params?.data?.newPrdDesc ?? "";
        const displayValue = newKit || newKitDescription
          ? `${newKit}${newKit && newKitDescription ? "-" : ""}${newKitDescription}`
          : "-";
        
        return <Tooltip title={displayValue} arrow>
          <span>{displayValue}</span>
        </Tooltip>
        },
        cellStyle: { textAlign: "left" },
      },
      {
        field: "newLocation",
        headerName: "Adjusted Location",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        width: 225,
        cellRenderer: NewLocationRenderer,
        cellStyle: { textAlign: "left" },
      },
      {
        field: "newProfitCenter",
        headerName: "Adjusted Profit Center",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        width: 225,
        cellRenderer: NewProfitCenterRenderer,
        cellStyle: { textAlign: "left" },
      },
      {
        field: "newForecastPartner",
        headerName: "Adjusted Forecast Partner",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        width: 225,
        cellRenderer: NewForecastPartnerRenderer,
        cellStyle: { textAlign: "left" },
      },
      {
        field: "sourceKeyFigurePercentage",
        headerName: "Source Key Figure %",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        width: 150,
        cellRenderer: SourceKeyFigureRenderer,
        cellStyle: { textAlign: "left" },
      },
      {
        field: "masterDataApprovalStatus",
        headerName: "Master Data Status",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        width: 200,
        cellRenderer: MasterDataStatusRenderer,
        cellStyle: { textAlign: "left" },
      },
      {
        field: "status",
        headerName: "Realignment Status",
        sortable: true,
        unSortIcon: true,
        suppressHeaderMenuButton: true,
        width: 200,
        cellRenderer: StatusRenderer,
        cellStyle: { textAlign: "left" },
      },
      {
      field: "masterDataApprovalDate",
      headerName: "Master Data Approval Date",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 225,
      cellRenderer: (params) => <span>{params.data.masterDataApprovalDate || "-"}</span>,
      cellStyle: { textAlign: "left" },
    },
    {
      field: "mdApprovedBy",
      headerName: "MD Approved By",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 200,
      cellRenderer: (params) => {
        const displayValue = params.data.mdApprovedBy || "-";
        return (
          <Tooltip title={displayValue} arrow>
            <span>{displayValue}</span>
          </Tooltip>
        );
      },
      cellStyle: { textAlign: "left" },
    },
    // {
    //   field: "reApprovedBy",
    //   headerName: "Realignment Approved By",
    //   sortable: true,
    //   unSortIcon: true,
    //   suppressHeaderMenuButton: true,
    //   filterParams: {
    //     filterOptions: ["contains", "startsWith", "endsWith"],
    //     defaultOption: "contains",
    //     buttons: ["apply", "reset"],
    //   },
    //   filter: "agTextColumnFilter",
    //   width: 200,
    //   cellRenderer: (params) => {
    //     const displayValue = params.data.reApprovedBy || "-";
    //     return (
    //       <Tooltip title={displayValue} arrow>
    //         <span>{displayValue}</span>
    //       </Tooltip>
    //     );
    //   },
    //   cellStyle: { textAlign: "left" },
    // },
    {
      field: "createdAt",
      headerName: "Data Created in AL",
      sortable: true,
      unSortIcon: true,
      suppressHeaderMenuButton: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
      },
      filter: "agTextColumnFilter",
      width: 200,
      cellRenderer: (params) => <span>{params.data.createdAt || "-"}</span>,
      cellStyle: { textAlign: "left" },
    },
    ],
    [
      locationOptions,
      profitCenterOptions,
      forecastPartnerOptions,
      masterDataApprovalStatus,
      activeEditRowId,
    ],
  );

  const pagination = false;
  const paginationPageSize = paginationModel.pageSize;
  const paginationPageSizeSelector = [5, 10, 20, 50, 100];

  const onGrid = useCallback((params) => {
    const gridApi = params.api;
    if (gridApi) {
      gridApi.autoSizeAllColumns(false);
    }
  }, []);

  const onPageChange = useCallback(
    (_, nextPage) => {
      setPage(nextPage);
    },
    [setPage],
  );

  const onRowsPerPageChange = useCallback(
    (event) => {
      const nextSize = Number(event.target.value);
      setPageSize(nextSize);
    },
    [setPageSize],
  );

  // const onSelectionChanged = useCallback(
  //   (params) => {
  //
  //     if (gridRef.current) {
  //       const selectedNodes = gridRef.current.api.getSelectedNodes();
  //       const selectedIds = selectedNodes.map((node) => node.data.id);
  //       setSelectedRows(selectedIds);
  //       dispatch(setSelectedRecords(selectedIds));
  //     }
  //   },
  //   [dispatch],
  // );

  const onSelectionChanged = useCallback(
    (params) => {
      if (activeEditRowId !== null) {
        params.api.deselectAll();
        toast.info("Save or cancel current edit to select other rows");
        return;
      }

      const selectedIds = params.api
        .getSelectedNodes()
        .map((node) => node.data.id);

      setSelectedRows(selectedIds);
      dispatch(setSelectedRecords(selectedIds));
    },
    [activeEditRowId, dispatch],
  );

  // Memoize rowStyle to prevent recreation on every render
  const getRowStyle = useCallback(
    (params) => {
      if (params.data.id === activeEditRowId) {
        return { backgroundColor: "#fff8e1" }; // light yellow
      }
      if (masterDataApprovalStatus[params.data.id] === true) {
        return { backgroundColor: "#e8f5e9" };
      }
      return {};
    },
    [activeEditRowId, masterDataApprovalStatus],
  );

  const pendingRecordsData = useMemo(
    () => pendingRecords || [],
    [pendingRecords],
  );

  useEffect(() => {
    if (
      paginationModel.page > 0 &&
      pendingRecords.length === 0 &&
      !loading
    ) {
      setPage(Math.max(0, paginationModel.page - 1));
    }
  }, [loading, paginationModel.page, pendingRecords.length, setPage]);

  const resetEditState = () => {
    editedValuesRef.current = {};
    editedRecordIdsRef.current = new Set();
    setActiveEditRowId(null);
  };

  const handleSaveCancel = () => {
    resetEditState();
    setSaveConfirmOpen(false);
    toast.info("Edits discarded");
  };

  const handleMasterDataSaveCancel = () => {
    setSelectedRecords([]);
    setApproveConfirmOpen({ open: false, actionType: "" });
    toast.info(approveConfirmOpen.actionType === "MD" ? "Master Data approval cancelled" : "Realignment approval cancelled");
  };

const locationFilterOptions = useMemo(
  () => {
    const locationIds = [...new Set(pendingRecordsData.map(r => r.location).filter(Boolean))];
    return locationIds.map(locId => ({
      value: locId,
      label: `${locId}-${locationOptions.find(l => l.locationId === locId)?.location || locId}`
    }));
  },
  [pendingRecordsData, locationOptions]
);

const regionFilterOptions = useMemo(
  () => [...new Set(pendingRecordsData.map(r => r.region).filter(Boolean))],
  [pendingRecordsData]
);

const kitFilterOptions = useMemo(
  () => {
    const kitCodes = [...new Set(pendingRecordsData.map(r => r.newKit).filter(Boolean))];
    return kitCodes.map(kit => {
      const kitRecord = pendingRecordsData.find(r => r.newKit === kit);
      return {
        value: kit,
        label: `${kit}${kitRecord?.newPrdDesc ? '-' + kitRecord.newPrdDesc : ''}`
      };
    });
  },
  [pendingRecordsData]
);

const filteredRecords = useMemo(() => {
  const locationValues = filters.location.map(item => item.value);
  const kitValues = filters.kit.map(item => item.value);
  return pendingRecordsData.filter((r) =>
    (locationValues.length === 0 || locationValues.includes(r.location)) &&
    (filters.region.length === 0 || filters.region.includes(r.region)) &&
    (kitValues.length === 0 || kitValues.includes(r.newKit))
  );
}, [pendingRecordsData, filters]);

const clearFilterVisibility = filters.location.length > 0 || filters.region.length > 0 || filters.kit.length > 0;

  return (
    <div>
      <Box sx={{ display: "flex", mb: 1, alignItems: "center", justifyContent: "space-between" }}>
        {/* Filter Inputs */}
    <Box sx={{ display: "flex", mb: 1, flexDirection: "column" }}>
      <Box sx={{ display: "flex", gap: 2}}><Typography style={{fontSize:"14px", padding: "6px 8px"}}>Filter Options: </Typography>
      {clearFilterVisibility && (
        <Button
          variant="text"
          onClick={() => setFilters({ location: [], region: [], kit: [] })}
        >
          Clear Filters
        </Button>
      )}
      </Box>
      <Box sx={{ display: "flex", gap: 1}}>
        <Autocomplete
        multiple
        size="small"
        limitTags={2}
        getLimitTagsText={(more) => `+${more} more`}
        options={locationFilterOptions}
        value={filters.location}
        onChange={(_, v) => setFilters(f => ({ ...f, location: v }))}
        getOptionLabel={(option) => option.label || ""}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        sx={{
              "& .MuiOutlinedInput-root": {
                maxHeight: "70px",
                overflowY: "auto",
                alignItems: "flex-start",
                flexWrap: "wrap",
                width: "250px",
              },
            }}
        renderInput={(p) => <TextField {...p} label="Location" placeholder="Select locations" />}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <input type="checkbox" checked={selected} readOnly style={{ marginRight: "8px" }} />
            {option.label}
          </li>
        )}
        disableCloseOnSelect
      />

      <Autocomplete
        multiple
        size="small"
        limitTags={2}
        getLimitTagsText={(more) => `+${more} more`}
        options={regionFilterOptions}
        value={filters.region}
        onChange={(_, v) => setFilters(f => ({ ...f, region: v }))}
        sx={{
              "& .MuiOutlinedInput-root": {
                maxHeight: "70px",
                overflowY: "auto",
                alignItems: "flex-start",
                flexWrap: "wrap",
                width: "250px",
              },
            }}
        renderInput={(p) => <TextField {...p} label="Region" placeholder="Select regions" />}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <input type="checkbox" checked={selected} readOnly style={{ marginRight: "8px" }} />
            {option}
          </li>
        )}
        disableCloseOnSelect
      />

      <Autocomplete
        multiple
        size="small"
        limitTags={2}
        getLimitTagsText={(more) => `+${more} more`}
        options={kitFilterOptions}
        value={filters.kit}
        onChange={(_, v) => setFilters(f => ({ ...f, kit: v }))}
        getOptionLabel={(option) => option.label || ""}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        sx={{
              "& .MuiOutlinedInput-root": {
                maxHeight: "70px",
                overflowY: "auto",
                alignItems: "flex-start",
                flexWrap: "wrap",
                width: "250px",
              },
            }}
        renderInput={(p) => <TextField {...p} label="New Kit" placeholder="Select kits" />}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <input type="checkbox" checked={selected} readOnly style={{ marginRight: "8px" }} />
            {option.label}
          </li>
        )}
        disableCloseOnSelect
      />
      </Box>
    </Box>
      {/* Top Action Bar */}
      <Box sx={{marginTop: "24px",display: "flex", gap: 2, justifyContent: "end" }}>
        {/* Save Status Chip */}
        {/* {saveStatus && (
          <Chip
            {...(saveStatusType === "success" && { icon: <CheckCircle /> })}
            label={saveStatus}
            color={saveStatusType === "success" ? "success" : "error"}
            variant="outlined"
          />
        )} */}

        <ButtonComponent
          bgColor="black"
          color="white"
          startIcon={<Save />}
          label={savingRecord ? "Saving..." : "Save"}
          fullWidth={false}
          onClick={() => setSaveConfirmOpen(true)}
          disabled={activeEditRowId === null || savingRecord}
        />

        <Box sx={{ position: "relative" }}>
          <ButtonComponent
            bgColor={activeEditRowId !== null ? "#f44336" : "black"}
            color="white"
            label={activeEditRowId !== null ? "Cancel" : "Actions"}
            endIcon={activeEditRowId === null ? <MoreVert /> : undefined}
            fullWidth={false}
            onClick={
              activeEditRowId !== null
                ? handleSaveCancel
                : handleActionMenuClick
            }
            disabled={
              savingRecord ||
              (activeEditRowId === null && selectedRows.length === 0)
            }
          />
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleActionMenuClose}
          >
            {/* <MenuItem disabled>Master Data</MenuItem> */}
            <MenuItem
              onClick={() => setApproveConfirmOpen({ open: true, actionType: "MD" })}
              disabled={isMasterDataDisabled}
            >
              Approve Master Data
            </MenuItem>
            {/* <MenuItem onClick={() => handleRejectOpen("MD")}>Reject</MenuItem> */}
            {/* <MenuItem
              disabled
              sx={{ opacity: isRealignmentDisabled ? 0.5 : 1 }}
            >
              Realignment
            </MenuItem> */}
            <MenuItem
              onClick={() => setApproveConfirmOpen({ open: true, actionType: "REALIGNMENT" })}
              disabled={isRealignmentDisabled}
            >
              Approve Realignment
            </MenuItem>
            <MenuItem
              onClick={() => handleRejectOpen()}
              // disabled={isRealignmentDisabled}
            >
              Reject Record
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "70vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <DataTable
            ref={gridRef}
            columns={commonColumns}
            rows={filteredRecords?filteredRecords:pendingRecordsData}
            tableHeight={"70vh"}
            suppressMovableColumns={true}
            suppressHorizontalScroll={false}
            suppressPaginationPanel={true}
            pagination={pagination}
            paginationPageSize={paginationPageSize}
            paginationPageSizeSelector={paginationPageSizeSelector}
            onGridReady={onGrid}
            onSelectionChanged={onSelectionChanged}
            domLayout="normal"
            getRowStyle={getRowStyle}
          />

          <TablePagination
            component="div"
            count={totalElements}
            page={paginationModel.page}
            onPageChange={onPageChange}
            rowsPerPage={paginationModel.pageSize}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={paginationPageSizeSelector}
            ActionsComponent={ServerTablePaginationActions}
            sx={{
              "& .MuiTablePagination-input": {
                border: "1px solid #c7ccd1",
                borderRadius: "8px",
                px: 1,
                py: 0.25,
                mr: 1,
              },
              "& .MuiTablePagination-select": {
                fontWeight: 700,
              },
              "& .MuiTablePagination-selectIcon": {
                right: "4px",
              },
            }}
          />

          {/* Reject Dialog */}
          <Dialog
            open={rejectDialog.open}
            onClose={() => setRejectDialog({ ...rejectDialog, open: false })}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Rejection Remarks</DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Enter rejection remarks..."
                value={rejectDialog.remarks}
                onChange={(e) =>
                  setRejectDialog({ ...rejectDialog, remarks: e.target.value })
                }
                sx={{ mt: 2 }}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() =>
                  setRejectDialog({ ...rejectDialog, open: false })
                }
              >
                Cancel
              </Button>
              <Button
                onClick={handleRejectConfirm}
                variant="contained"
                color="error"
                disabled={loading || !rejectDialog.remarks.trim()}
              >
                {loading ? <CircularProgress size={24} /> : "Reject"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* Save Confirmation Dialog */}
      <Dialog
        open={saveConfirmOpen}
        onClose={() => setSaveConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Save</DialogTitle>

        <DialogContent dividers>
          <Typography>Are you sure you want to save the changes?</Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleSaveCancel}>Cancel</Button>

          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={savingRecord}
          >
            {savingRecord ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Master Data Approval Confirmation Dialog */}
      <Dialog
        open={approveConfirmOpen.open}
        onClose={() => setApproveConfirmOpen({ open: false, actionType: "" })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Approval</DialogTitle>

        <DialogContent dividers>
          <Typography>
            {approveConfirmOpen.actionType === "MD"
              ? "Are you sure you want to approve the Master Data?"
              : "Are you sure you want to approve the Realignment request?"}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleMasterDataSaveCancel}>Cancel</Button>

          <Button
            onClick={() => handleApprove(approveConfirmOpen.actionType)}
            variant="contained"
            color="primary"
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RealignmentList;
